import React from "react";
import { ReactComponent as IconClose } from "@assets/Icons/IconLeft.svg";
import { ReactComponent as IconLeft } from "@assets/Icons/DoubleLeft.svg";
import { ReactComponent as IconRight } from "@assets/Icons/Double-right.svg";
import SideMenuAccountCard from "./AccountCard/SideMenuAccountCard";
import SideMenuTenantCard from "./AccountCard/SideMenuTenantCard";
import {
  CHROME_PADDING,
  CHROME_SEAM_ABOVE_NAV,
  CHROME_STACK,
  COLLAPSED_SQUARE,
  FOCUS_RING,
  ROW_ICON_STANDALONE,
  TRANSITION_COLORS,
} from "../sideMenuDensity";

type Auth0ForPartner = {
  isLoading: boolean;
  isAuthenticated: boolean;
  getAccessTokenSilently: () => Promise<string>;
};

export type SideMenuTopChromeProps = {
  isMobileView: boolean;
  isExpanded: boolean;
  logoSrc: string;
  onLogoError: () => void;
  onToggleExpand: () => void;
  onCloseMobile: () => void;
  productOptions: string[];
  selectedProduct: string;
  onSelectProduct: (value: string) => void;
  tenantType: string;
  pathname: string;
  auth0: Auth0ForPartner;
};

const SideMenuTopChrome: React.FC<SideMenuTopChromeProps> = ({
  isMobileView,
  isExpanded,
  logoSrc,
  onLogoError,
  onToggleExpand,
  onCloseMobile,
  productOptions,
  selectedProduct,
  onSelectProduct,
  tenantType,
  auth0,
}) => {
  return (
    <>
      {isMobileView && (
        <div
          className={`flex flex-col ${CHROME_STACK} ${CHROME_PADDING} ${CHROME_SEAM_ABOVE_NAV}`}
        >
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <SideMenuAccountCard
                mobile
                logoSrc={logoSrc}
                onLogoError={onLogoError}
                productOptions={productOptions}
                selectedProduct={selectedProduct}
                onSelectProduct={onSelectProduct}
              />
            </div>
            <button
              type="button"
              onClick={onCloseMobile}
              aria-label="Close menu"
              className={`flex ${COLLAPSED_SQUARE} shrink-0 items-center justify-center rounded-md border border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-secondary ${TRANSITION_COLORS} ${FOCUS_RING} hover:bg-semantic-state-active`}
            >
              {/* Label-less bordered control, so it takes the standalone size
                  rather than the in-row chevron's. */}
              <IconClose className={ROW_ICON_STANDALONE} />
            </button>
          </div>
          <SideMenuTenantCard tenantType={tenantType} auth0={auth0} mobile />
        </div>
      )}

      {!isMobileView &&
        (isExpanded ? (
          /* Height is intrinsic: the old fixed `h-[132px]` reserved chrome space
             whether or not the tenant card rendered (it is absent for CUSTOMER
             tenants), which was the single largest fixed cost in the rail. */
          <div
            /* The seam overrides CHROME_PADDING's 12px bottom for this surface
               only. With the divider gone and NAV_PADDING's top at 0, this is the
               entire distance between the chrome and the first nav row. Scoped
               here rather than pushed into CHROME_PADDING because that constant
               also sets the space *above* the switcher and is shared with the
               collapsed rail, the mobile drawer header and the Settings drill-in
               — none of which have a nav row directly beneath them. */
            className={`flex flex-col ${CHROME_STACK} ${CHROME_PADDING} ${CHROME_SEAM_ABOVE_NAV}`}
          >
            <div className="flex items-center justify-between gap-2">
              <SideMenuAccountCard
                logoSrc={logoSrc}
                onLogoError={onLogoError}
                productOptions={productOptions}
                selectedProduct={selectedProduct}
                onSelectProduct={onSelectProduct}
              />
              <button
                type="button"
                onClick={onToggleExpand}
                aria-label="Collapse menu"
                className={`flex ${COLLAPSED_SQUARE} shrink-0 items-center justify-center rounded-md border border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-secondary ${TRANSITION_COLORS} ${FOCUS_RING} hover:bg-semantic-state-active`}
              >
                <IconLeft className={ROW_ICON_STANDALONE} />
              </button>
            </div>
            <SideMenuTenantCard tenantType={tenantType} auth0={auth0} />
          </div>
        ) : (
          /* Same gutters as the expanded chrome. At the 64px collapsed rail this
             leaves 40px of content for the 32px card and 28px toggle; the old
             `p-4` would have left 32px and clipped them. */
          <div className={`flex flex-col items-center ${CHROME_STACK} ${CHROME_PADDING}`}>
            <button
              type="button"
              onClick={onToggleExpand}
              aria-label="Expand menu"
              className={`flex ${COLLAPSED_SQUARE} shrink-0 items-center justify-center rounded-lg border border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-secondary ${TRANSITION_COLORS} ${FOCUS_RING} hover:bg-semantic-state-active`}
            >
              <IconRight className={ROW_ICON_STANDALONE} />
            </button>
            <SideMenuAccountCard
              compact
              logoSrc={logoSrc}
              onLogoError={onLogoError}
              productOptions={productOptions}
              selectedProduct={selectedProduct}
              onSelectProduct={onSelectProduct}
            />
            {/* Hidden while collapsed, but keeps the partner/customer selection
                bootstrapped via its hook. */}
            <SideMenuTenantCard tenantType={tenantType} auth0={auth0} collapsed />
          </div>
        ))}
    </>
  );
};

export default SideMenuTopChrome;
